import { FaClipboardList, FaEnvelopeOpenText, FaHourglassHalf, FaUsersCog } from "react-icons/fa";
import type { TFunction } from "i18next";
import type { DashboardCounts, GiteaSummary, MailchimpStatus, NoctisOutboxStatus, NoctisReachability, NoctisStatus } from "../types/dashboard.types";
import { safeErrorText } from "../utils/dashboardStatus.utils";
import ActionTile from "./ActionTile";
import GiteaActionTile from "./GiteaActionTile";
import NoctisActionTile from "./NoctisActionTile";
import MailchimpActionTile from "./MailchimpActionTile";
import EBoekhoudenActionTile from "./EBoekhoudenActionTile";

interface DashboardOpenActionsProps {
  t: TFunction;
  locale: string;
  counts: DashboardCounts;
  actionsLoading: boolean;
  actionsError: string | null;
  permissions: {
    canManageApplications: boolean;
    canManageMutations: boolean;
    canViewWaitingList: boolean;
    canSeeGiteaIssues: boolean;
    canSeeNoctis: boolean;
    canSeeEBoekhouden?: boolean;
    canSeeMailchimp: boolean;
  };
  openActionsTileCount: number;
  openActionsTotal: number;
  gitea: { loading: boolean; error: string | null; summary: GiteaSummary; openCount: number };
  noctis: { loading: boolean; error: string | null; reachability: NoctisReachability | null; outbox: NoctisOutboxStatus | null; status: NoctisStatus | null; queueTotal: number };
  mailchimp: { loading: boolean; error: string | null; status: MailchimpStatus | null; neverSynced: boolean };
  onNavigate: (path: string) => void;
}

const DashboardOpenActions = ({ t, locale, counts, actionsLoading, actionsError, permissions, openActionsTileCount, openActionsTotal, gitea, noctis, mailchimp, onNavigate }: DashboardOpenActionsProps) => {
  if (!(permissions.canManageApplications || permissions.canManageMutations || permissions.canViewWaitingList || permissions.canSeeGiteaIssues || permissions.canSeeNoctis || permissions.canSeeMailchimp || permissions.canSeeEBoekhouden)) {
    return null;
  }

  return (
    <section className="dashboard-enter rounded-[28px] border border-slate-200/80 bg-white/95 p-6 shadow-[0_14px_35px_rgba(15,23,42,0.08)] [animation-delay:80ms] dark:border-white/10 dark:bg-slate-950/70 md:p-8">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="flex items-center gap-2 text-lg font-semibold text-slate-900 dark:text-white">
          <FaClipboardList className="text-lg text-indigo-500" />
          {t("dashboard.actions.title", { defaultValue: "Open actions" })}
        </h3>
        {actionsLoading ? (
          <div className="h-5 w-28 rounded-full bg-slate-100 motion-safe:animate-pulse dark:bg-white/10" aria-label={t("common.loading", { defaultValue: "Loading..." })} />
        ) : null}
      </div>

      {actionsError ? (
        <div className="mb-2 rounded-lg border border-red-200 bg-red-50 p-3 text-red-700 dark:border-red-900 dark:bg-red-900/30 dark:text-red-200">
          {t("dashboard.actions.error", { defaultValue: "Could not load actions." })} {safeErrorText(actionsError, "")}
        </div>
      ) : null}

      <div className={`grid gap-4 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 ${openActionsTileCount === 1 ? "mx-auto max-w-3xl" : ""}`}>
        {permissions.canManageApplications ? (
          <ActionTile icon={<FaEnvelopeOpenText />} label={t("dashboard.actions.applications.label", { defaultValue: "New applications" })} count={counts.applications} ctaLabel={t("dashboard.actions.applications.view", { defaultValue: "View" })} onClick={() => onNavigate("/school/applications")} />
        ) : null}
        {permissions.canManageMutations ? (
          <ActionTile icon={<FaUsersCog />} label={t("dashboard.actions.mutations.label", { defaultValue: "Pending mutations" })} count={counts.mutations} ctaLabel={t("dashboard.actions.mutations.review", { defaultValue: "Review" })} onClick={() => onNavigate("/administratie/mutaties")} />
        ) : null}
        {permissions.canViewWaitingList ? (
          <ActionTile icon={<FaHourglassHalf />} label={t("dashboard.actions.waiting.label", { defaultValue: "Waiting list" })} count={counts.waiting} ctaLabel={t("dashboard.actions.waiting.manage", { defaultValue: "Manage" })} onClick={() => onNavigate("/school/waiting-list")} />
        ) : null}
        {permissions.canSeeGiteaIssues ? <GiteaActionTile loading={gitea.loading} error={gitea.error} summary={gitea.summary} total={gitea.openCount} onClick={() => onNavigate("/gitea")} t={t} /> : null}
        {permissions.canSeeNoctis ? (
          <NoctisActionTile
            loading={noctis.loading}
            error={noctis.error}
            reachability={noctis.reachability}
            queueTotal={noctis.queueTotal}
            lastSuccessAt={noctis.status?.lastSuccessAt ?? noctis.status?.last_success_at ?? null}
            nextAttemptAt={noctis.outbox?.nextAttemptAt ?? noctis.outbox?.next_attempt_at ?? null}
            locale={locale}
            onClick={() => onNavigate("/noctis/queue")}
            t={t}
          />
        ) : null}
        {permissions.canSeeMailchimp ? <MailchimpActionTile loading={mailchimp.loading} error={mailchimp.error} neverSynced={mailchimp.neverSynced} status={mailchimp.status} locale={locale} onClick={() => onNavigate("/integrations/mailchimp")} t={t} /> : null}
        {permissions.canSeeEBoekhouden ? <EBoekhoudenActionTile locale={locale} onClick={() => onNavigate("/integrations/eboekhouden")} t={t} /> : null}
      </div>

      {!actionsLoading && !actionsError && openActionsTotal === 0 ? (
        <p className="mt-4 text-slate-500 dark:text-slate-300">
          {t("dashboard.actions.empty", { defaultValue: "No open actions. Everything is up to date!" })}
        </p>
      ) : null}
    </section>
  );
};

export default DashboardOpenActions;
